<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";

const pairs = [
  { words: "Accept / Except", usage: "I accept your apology. / Everyone except Ali was present." },
  { words: "Affect / Effect", usage: "Heat affects health. / The effect was visible." },
  { words: "Principal / Principle", usage: "The principal addressed the students. / Honesty is a good principle." },
  { words: "Advice / Advise", usage: "He gave me good advice. / I advise you to work hard." },
  { words: "Loose / Lose", usage: "This shirt is loose. / Do not lose your books." },
  { words: "Quite / Quiet", usage: "The paper was quite easy. / Please keep quiet in the class." },
  { words: "Borrow / Lend", usage: "May I borrow your pen? / Please lend me your pen." },
  { words: "Later / Latter", usage: "We shall discuss it later. / Of the two choices, the latter is better." },
];

const examTips = [
  "Har pair ke liye meaning difference samajhna first step hai.",
  "Board answer me dono words ko separate sentences me use karo.",
  "Spelling similarity ki wajah se confusion hoti hai, is liye sentence context yaad rakhna helpful hota hai.",
];

const morePairs = [
  "Compliment / Complement",
  "Dairy / Diary",
  "Personal / Personnel",
  "Stationary / Stationery",
  "Weather / Whether",
];

const practiceQuestions = [
  {
    prompt: "Choose the right word: Please keep ___ while the teacher is speaking.",
    answer: "quiet",
    reason: "`Quiet` silence ko show karta hai, jab ke `quite` ka meaning `fairly` hota hai.",
  },
  {
    prompt: "Choose the right word: I want to ___ a book from the library.",
    answer: "borrow",
    reason: "`Borrow` lene ke liye use hota hai, `lend` dene ke liye.",
  },
  {
    prompt: "Choose the right word: The shop sells office ___.",
    answer: "stationery",
    reason: "`Stationery` writing materials ke liye use hota hai, `stationary` ka meaning non-moving hota hai.",
  },
];

const correctionChecklist = [
  "Har word ka part of speech identify karo.",
  "Sentence me meaning fit ho raha hai ya nahi check karo.",
  "Spelling dekh kar guess mat karo; context se decide karo.",
];

const mcq = {
  question: "Choose the correct sentence pair.",
  options: [
    { label: "a", text: "I except your request. / All accept Ali were absent.", verdict: "incorrect", reason: "Dono words wrong context me use hue hain." },
    { label: "b", text: "I accept your request. / All except Ali were absent.", verdict: "correct", reason: "Accept request ke sath aur except exclusion ke sath correctly use hua hai." },
    { label: "c", text: "The affect was good. / Heat effects health.", verdict: "incorrect", reason: "Affect/effect dono forms wrong hain." },
    { label: "d", text: "The principle of the school is absent. / Honesty is a good principal.", verdict: "incorrect", reason: "Principal/principle ulte use hue hain." },
  ],
};

usePageSeo({
  title: "11th Class English Pair of Words | Meanings, Usage, Practice",
  description: "11th class English pair of words page with meanings, sentence usage, practice questions, MCQ help and exam tips.",
  keywords: "pair of words 11th class english, class 11 pair of words, english grammar pair of words, pair of words mcqs",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Grammar", item: "/11th/english/grammar/" },
    { name: "Pair of Words", item: "/11th/english/grammar/pair-of-words/" },
  ],
});
</script>

<template>
  <PageContainer>
    <EnglishHero
      headline="Grammar Task"
      title="Pair of Words"
      description="Yeh page pair of words ko board-style usage ke sath revise karne ke liye banaya gaya hai. Focus correct meaning aur sentence context par hai."
    />

    <EnglishSection title="Practice Set" description="Use these word pairs with sentence context instead of memorizing them in isolation.">
      <EnglishGrid>
        <EnglishCard v-for="item in pairs" :key="item.words" :title="item.words" :description="item.usage" />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection title="Exam Tips" description="Keep these rules in mind when pair-of-words appears in the paper.">
      <EnglishGrid>
        <EnglishCard v-for="item in examTips" :key="item" title="Tip" :description="item" />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection title="More Pairs to Revise" description="Use these as extra recall prompts while revising.">
      <EnglishGrid>
        <EnglishCard v-for="item in morePairs" :key="item" title="Pair" :description="item" />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection title="Quick Practice Questions" description="A few fast checks before you move into full MCQ revision.">
      <EnglishGrid>
        <EnglishCard v-for="item in practiceQuestions" :key="item.prompt" :title="item.prompt" :description="`Answer: ${item.answer}. ${item.reason}`" />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection title="How to Eliminate Wrong Options" description="These checks reduce guesswork when options look similar.">
      <EnglishGrid>
        <EnglishCard v-for="item in correctionChecklist" :key="item" title="Checklist" :description="item" />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection title="MCQ with Explanation" description="Use this example to see how pair-of-words MCQs should be reasoned.">
      <EnglishLinkList :links="mcq.options.map((option) => ({ label: `${option.label}. ${option.text} ${option.verdict === 'correct' ? 'Correct:' : 'Not correct:'} ${option.reason}` }))" />
    </EnglishSection>
  </PageContainer>
</template>
